<script setup lang="ts">
import { ref } from 'vue';

import { useI18n } from 'vue-i18n';
import {
  QuestionCircleIcon,
} from '@thehotelsnetwork/icons/vue/linear';

import { visitDatesConfig, minVisitDatesMonth, maxVisitDatesMonth } from '@/utils/visitDatesUtils';

import {
  isCtpDateSelectorDisabled,
  minCTPMonth,
  maxCTPMonth,
  compareToPastConfig,
} from '@/utils/ctpDatesUtils';

import useFilterState from '@/state/useFilterState';
import useSectionState from '@/state/useSectionState';
import useUserState from '@/state/useUserState';
import DatesSelector from '@/modules/DataSections/components/Calendar/DatesSelector.vue';

import { track } from '@/utils/analytics';
import navigate from '@/utils/navigate';
import { premiumLandingUrl } from '@/config/redirectUrls';

const { t } = useI18n();
const {
  dateBegin,
  dateEnd,
  ctpDateBegin,
  ctpDateEnd,
  applyCtpStayDates,
  ctpTag,
} = useFilterState();

const {
  isFreeCalendarView,
} = useSectionState();
const { showUpselling } = useUserState();

const showTooltip = ref(false);
const upsellingText = 'Subscribe to Personalization to see unlimited dates';

const handleUpgradeClick = () => {
  track('premium - click - geolocation');

  navigate(premiumLandingUrl, { external: true, newTab: true });
};

</script>

<template>
  <div class="flex items-center gap-4">
    <DatesSelector
      :disabled="isFreeCalendarView"
      :d-b="dateBegin"
      :d-e="dateEnd"
      :min-month="minVisitDatesMonth ?? 0"
      :max-month="maxVisitDatesMonth"
      :config="visitDatesConfig"
    />
    <LpTooltip
      v-model="showTooltip"
      size="small"
      placement="bottom"
      mode="manual"
      :max-width="248"
      :min-width="248"
      :nowrap="false"
      custom-wrapper
      @mouseover="showTooltip = isCtpDateSelectorDisabled && showUpselling"
      @focusin="showTooltip = isCtpDateSelectorDisabled && showUpselling"
      @mouseleave="showTooltip = false"
      @blur="showTooltip = false"
    >
      <template #trigger>
        <DatesSelector
          :disabled="isFreeCalendarView || isCtpDateSelectorDisabled"
          :d-b="ctpDateBegin"
          :d-e="ctpDateEnd"
          :min-month="minCTPMonth ?? 0"
          :max-month="maxCTPMonth"
          :config="compareToPastConfig"
        >
          <template #additionalOptions="{ triggerDates }">
            <div class="flex items-center gap-2 p-2 text-sm text-base-700 whitespace-nowrap">
              {{ t('Apply to Stay Dates filter') }}

              <LpTooltip
                size="small"
                mode="hover"
                :max-width="350"
                :min-width="350"
                placement="bottom"
                custom-wrapper
              >
                <template #trigger>
                  <QuestionCircleIcon class="w-5 h-5" />
                </template>
                <template
                  #tooltip
                >
                  <LpCard class="p-5 text-sm">
                    <p class="text-base font-medium">
                      {{ t('ctp.staydates-filter.tooltip') }}
                    </p>
                  </LpCard>
                </template>
              </LpTooltip>

              <LpSwitch
                v-model="applyCtpStayDates"
                :disabled="ctpTag !== 'pp' && ctpTag !== 'py'"
                color="white"
                size="medium"
                @click="() => {
                  triggerDates({ label: 'Apply to stay dates', tag: ctpTag });
                }"
              />
            </div>
          </template>
        </DatesSelector>
      </template>
      <template
        #tooltip="{ closeMenu }"
      >
        <LpCard class="bg-grape-100 text-grape-500 p-2">
          <p
            class="upselling"
            v-html="upsellingText"
          />
          <a
            class="underline !font-bold cursor-pointer text-sm"
            @click="() => {
              closeMenu();
              handleUpgradeClick();
            }"
            @keydown="() => {
              closeMenu();
              handleUpgradeClick();
            }"
          >
            {{ t('Upgrade now') }}
          </a>
        </LpCard>
      </template>
    </LpTooltip>
  </div>
</template>
